import { ChevronLeft, ChevronRight } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";

type ReviewFooterProps = {
  /** False while the brief is missing a required criterion. */
  canSubmit?: boolean;
  current: number;
  isSavingDraft?: boolean;
  isSubmitting?: boolean;
  onBackToChat?: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  onSaveToDrafts?: () => void;
  onSelect?: (variation: number) => void;
  onSubmit?: () => void;
  total: number;
};

export function ReviewFooter({
  canSubmit = false,
  current,
  isSavingDraft = false,
  isSubmitting = false,
  onBackToChat,
  onNext,
  onPrev,
  onSaveToDrafts,
  onSelect,
  onSubmit,
  total,
}: ReviewFooterProps) {
  const pages = Array.from({ length: total }, (_, index) => index + 1);

  return (
    <footer className="sticky bottom-0 z-10 flex items-center justify-between border-t border-border-primary bg-bg-primary px-3xl py-4">
      <div className="flex items-center gap-lg">
        <span className="text-md font-semibold text-fg-brand-primary">
          Variation {current}
        </span>

        <div className="flex items-center gap-xs">
          <button
            aria-label="Previous variation"
            className="flex size-8 items-center justify-center rounded-lg text-text-secondary outline-hidden transition-colors hover:bg-bg-primary-hover disabled:cursor-not-allowed disabled:text-fg-disabled disabled:hover:bg-transparent"
            disabled={current <= 1}
            onClick={onPrev}
            type="button"
          >
            <ChevronLeft className="size-5" />
          </button>

          {pages.map((page) => (
            <button
              aria-current={page === current ? "true" : undefined}
              className={twMerge(
                "flex size-8 items-center justify-center rounded-full text-sm font-medium text-text-secondary outline-hidden transition-colors hover:bg-bg-primary-hover",
                page === current &&
                  "bg-utility-brand-50 font-semibold text-fg-brand-primary",
              )}
              key={page}
              onClick={() => onSelect?.(page)}
              type="button"
            >
              {page}
            </button>
          ))}

          <button
            aria-label="Next variation"
            className="flex size-8 items-center justify-center rounded-lg text-text-secondary outline-hidden transition-colors hover:bg-bg-primary-hover disabled:cursor-not-allowed disabled:text-fg-disabled disabled:hover:bg-transparent"
            disabled={current >= total}
            onClick={onNext}
            type="button"
          >
            <ChevronRight className="size-5" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {onBackToChat ? (
          <button
            className="cursor-pointer text-sm font-semibold text-text-secondary hover:text-text-primary"
            onClick={onBackToChat}
            type="button"
          >
            Back to chat
          </button>
        ) : null}
        {onSaveToDrafts ? (
          <Button
            color="gray"
            isDisabled={isSavingDraft}
            onPress={onSaveToDrafts}
            variant="secondary"
          >
            {isSavingDraft ? "Saving..." : "Save to drafts"}
          </Button>
        ) : null}
        <Button
          isDisabled={!canSubmit || isSubmitting}
          onPress={onSubmit}
          variant="primary"
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
      </div>
    </footer>
  );
}
